import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { suppliersApi } from '../api';
import { useFetch } from '../hooks/useFetch';
import toast from 'react-hot-toast';

function SupplierModal({ onClose, onSaved, supplier }) {
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    gstNumber: '',
    status: 'active',
    notes: ''
  });

  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (supplier) {
      setForm({
        name: supplier.name || '',
        phone: supplier.phone || '',
        email: supplier.email || '',
        address: supplier.address || '',
        gstNumber: supplier.gstNumber || '',
        status: supplier.status || 'active',
        notes: supplier.notes || ''
      });
    }
  }, [supplier]);

  const handleSubmit = async () => {
    setLoading(true);

    // ✅ Validation
    if (!form.name) {
      return toast.error("Supplier name is required");
    }

    if (!form.phone) {
      return toast.error("Phone number is required");
    }

    // ✅ Phone validation (India)
    if (!/^[6-9]\d{9}$/.test(form.phone)) {
      return toast.error("Enter valid 10-digit phone number");
    }
    try {
      const payload = {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        address: form.address || null,
        gstNumber: form.gstNumber || null,
        status: form.status,
        notes: form.notes || null
      };

      if (supplier) {
        await suppliersApi.update(supplier.id, payload);
        toast.success('Supplier updated!');
      } else {
        await suppliersApi.create(payload);
        toast.success('Supplier created!');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save supplier');
    }
    setLoading(false);
  };

  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 500 }}>
        <div className="modal-header">
          <h3>{supplier ? 'Edit Supplier' : 'Create New Supplier'}</h3>
          <button className="btn btn-secondary btn-sm btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <div className="form-row form-row-2">
            <div className="form-group">
              <label className="form-label">Supplier Name *</label>
              <input
                type="text"
                className="form-control"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                placeholder="Enter supplier name"

              />
            </div>
            <div className="form-group">
              <label className="form-label">Phone *</label>
              <input
                type="text"
                className="form-control"
                value={form.phone}
                onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                placeholder="Enter phone number"

              />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                className="form-control"
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="Enter email address"
              />
            </div>
            <div className="form-group">
              <label className="form-label">GST Number</label>
              <input
                type="text"
                className="form-control"
                value={form.gstNumber}
                onChange={(e) => setForm((f) => ({ ...f, gstNumber: e.target.value }))}
                placeholder="Enter GST number"
              />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Address</label>
            <textarea
              className="form-control"
              rows={2}
              value={form.address}
              onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
              placeholder="Enter address"
            />
          </div>
          <div className="form-row form-row-2">
            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                className="form-control"
                value={form.status}
                onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea
              className="form-control"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="Additional notes..."
            />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
            {loading ? 'Saving...' : supplier ? 'Update Supplier' : 'Create Supplier'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteConfirmModal({ onClose, onConfirm, supplierName, loading }) {
  return (
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 400 }}>
        <div className="modal-header">
          <h3>Delete Supplier</h3>
          <button className="btn btn-secondary btn-sm btn-icon" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          <p>Are you sure you want to delete <strong>{supplierName}</strong>?</p>
          <p className="text-muted text-sm">This action cannot be undone.</p>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-danger" onClick={onConfirm} disabled={loading}>
            {loading ? 'Deleting...' : 'Delete'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Suppliers() {
  const navigate = useNavigate();
  const [modal, setModal] = useState(false);
  const [editSupplier, setEditSupplier] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);

  const fetchSuppliers = useCallback(() => suppliersApi.getAll({ search, status: statusFilter, page, limit: 20 }), [search, statusFilter, page]);
  const { data, loading, refetch } = useFetch(fetchSuppliers, [search, statusFilter, page]);

  const suppliers = data?.data?.suppliers || data?.suppliers || data?.data || [];
  const pagination = data?.pagination;

  const handleEdit = (supplier) => {
    setEditSupplier(supplier);
    setModal(true);
  };

  const handleView = (supplier) => {
    navigate(`/suppliers/${supplier.id}`);
  };

  const handleDelete = (supplier) => {
    setSupplierToDelete(supplier);
    setDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!supplierToDelete) return;
    try {
      await suppliersApi.delete(supplierToDelete.id);
      toast.success('Supplier deleted!');
      setDeleteModal(false);
      setSupplierToDelete(null);
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete supplier');
    }
  };

  const closeModal = () => {
    setModal(false);
    setEditSupplier(null);
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Suppliers</h1>
        <button className="btn btn-primary" onClick={() => setModal(true)}>+ New Supplier</button>
      </div>

      <div className="filters-bar">
        <div className="search-input" style={{ flex: 1, minWidth: 200 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
          <input className="form-control" placeholder="Search suppliers..." value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
        </div>
        <select className="form-control" style={{ width: 140 }} value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      <div className="card">
        {loading ? (
          <div className="empty-state"><div className="spinner" /></div>
        ) : (
          <div className="table-wrapper" style={{ border: 'none', borderRadius: 0 }}>
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Phone</th>
                  <th>Email</th>
                  <th>GST Number</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {suppliers.length === 0 ? (
                  <tr><td colSpan={6}><div className="empty-state"><div className="empty-icon">🏭</div><p>No suppliers found</p></div></td></tr>
                ) : suppliers.map((supplier) => (
                  <tr key={supplier.id}>
                    <td className="font-semibold">{supplier.name}</td>
                    <td>{supplier.phone}</td>
                    <td className="text-muted">{supplier.email || '—'}</td>
                    <td className="text-muted">{supplier.gstNumber || '—'}</td>
                    <td>
                      <span className={`badge badge-${supplier.status}`}>
                        {supplier.status}
                      </span>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button className="btn btn-secondary btn-sm" onClick={() => handleView(supplier)}>
                          View
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={() => handleEdit(supplier)}>
                          Edit
                        </button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(supplier)}>
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {pagination && (
          <div className="pagination">
            <span>Page {pagination.page} of {pagination.totalPages} ({pagination.total} suppliers)</span>
            <div className="pagination-btns">
              <button className="btn btn-secondary btn-sm" disabled={!pagination.hasPrev} onClick={() => setPage(p => p - 1)}>← Prev</button>
              <button className="btn btn-secondary btn-sm" disabled={!pagination.hasNext} onClick={() => setPage(p => p + 1)}>Next →</button>
            </div>
          </div>
        )}
      </div>

      {modal && <SupplierModal onClose={closeModal} onSaved={refetch} supplier={editSupplier} />}
      {deleteModal && <DeleteConfirmModal onClose={() => setDeleteModal(false)} onConfirm={confirmDelete} supplierName={supplierToDelete?.name} loading={loading} />}
    </div>
  );
}
